// shared/pricing.ts: homebuild's pricing, locked by the owner (docs/DECISIONS.md "Pricing (owner, locked Oct 2026)",
// docs/SPEC.md 16.0). ONE plan, everything included, no feature tiers, at two rates:
//   founding  $499 a month billed annually ($5,988 a year), the first 10 builders in a market, locked for as long as the
//             subscription stays active; annual only.
//   standard  $699 a month, monthly or annual ($8,388 a year).
// There is no setup fee (owner, 2026-10-20: "Let's just drop it. No need for it."): paying the plan takes a builder live.
//
// Stripe holds the amounts that are charged: scripts/ops/stripe-setup.ts creates the three prices below under these
// lookup keys, and the Worker reads them back by lookup key (worker/src/billing/prices.ts). Every sum here runs on the
// amounts it is given, so a screen always shows what Stripe will charge. Money is in cents. Nothing here (or anywhere)
// states a future price: the standard rate may change for new builders later, and existing subscriptions keep theirs.

export type PlanId = "founding_annual" | "standard_monthly" | "standard_annual";
/** A Stripe price's lookup key: the three plans. */
export type PriceKey = PlanId;
export type PlanKind = "founding" | "standard";
export type PlanInterval = "month" | "year";

export const PLAN_IDS: readonly PlanId[] = ["founding_annual", "standard_monthly", "standard_annual"];
export const PRICE_KEYS: readonly PriceKey[] = PLAN_IDS;

/** Which rate each plan is and how often it bills. */
export const PLAN_SHAPE: Readonly<Record<PlanId, { kind: PlanKind; interval: PlanInterval }>> = {
  founding_annual: { kind: "founding", interval: "year" },
  standard_monthly: { kind: "standard", interval: "month" },
  standard_annual: { kind: "standard", interval: "year" },
};

/** The amounts the owner locked (Oct 2026), in cents: what stripe-setup.ts creates and what the tests assert. */
export const PRICE_BOOK: Readonly<Record<PriceKey, number>> = {
  founding_annual: 598_800,
  standard_monthly: 69_900,
  standard_annual: 838_800,
};

/** Founding spots in each market. */
export const FOUNDING_SPOTS = 10;

/** The amount of each price, in cents. */
export type PriceAmounts = Readonly<Record<PriceKey, number>>;

export function isPlanId(v: unknown): v is PlanId {
  return typeof v === "string" && (PLAN_IDS as readonly string[]).includes(v);
}

/** The plan of a kind and interval (founding is annual only). */
export function planOf(kind: PlanKind, interval: PlanInterval): PlanId {
  return kind === "founding" ? "founding_annual" : interval === "year" ? "standard_annual" : "standard_monthly";
}

/**
 * The plan the Billing section's Monthly | Yearly toggle stands for: yearly is founding when the builder is offered it,
 * else standard yearly; monthly is standard monthly. The server maps the toggle with its own answer to "offered", so a
 * browser can never pick founding for a builder who isn't offered it.
 */
export function planForToggle(interval: PlanInterval, foundingOffered: boolean): PlanId {
  return interval === "year" ? (foundingOffered ? "founding_annual" : "standard_annual") : "standard_monthly";
}

/** A plan's rate per month: the monthly price, or the annual price over 12 (founding: $5,988 / 12 = $499). */
export function monthlyRate(amounts: PriceAmounts, plan: PlanId): number {
  return PLAN_SHAPE[plan].interval === "month" ? amounts[plan] : Math.round(amounts[plan] / 12);
}

/** A year of the plan alone: 12 monthly charges, or the annual price. */
export function yearlyCost(amounts: PriceAmounts, plan: PlanId): number {
  return PLAN_SHAPE[plan].interval === "month" ? amounts[plan] * 12 : amounts[plan];
}

/** What starting a plan charges today, before tax: the plan's first period. */
export function todayCharge(amounts: PriceAmounts, plan: PlanId): { plan: number; total: number } {
  return { plan: amounts[plan], total: amounts[plan] };
}

/** What the founding rate saves in a year against the standard monthly rate: ($699 - $499) x 12 = $2,400. */
export function foundingSaving(amounts: PriceAmounts): number {
  return (amounts.standard_monthly - monthlyRate(amounts, "founding_annual")) * 12;
}
